<script setup>
/**WhatsappQRCode.vue */
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, minLength, maxLength } from '@vuelidate/validators';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useWhatsappAttributes } from 'dashboard/composables/useWhatsappAttributes';
import CustomAttributesEvolutionItem from 'dashboard/routes/dashboard/settings/inbox/components/CustomAttributesEvolutionItem.vue';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'dashboard/components-shadcn/new-york/ui/tabs';
import { useI18n } from 'vue-i18n';

// Reactive data
const phoneNumber = ref('');
const channelName = ref('');
const activeTab = ref('whatsapp');

// Composables
const store = useStore();
const router = useRouter();
const { t } = useI18n();

// Computed properties
const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);
const { attributesWhatsapp, whatsappAttributes, chatwootAttributes, updateAttribute } = useWhatsappAttributes();

// Custom validator for channel name
const isPlainString = (value) => {
  if (!value) return false;
  return /^[a-zA-Z0-9]+$/.test(value);
};

// Validation rules
const rules = {
  channelName: { 
    required,
    isPlainString: {
      $validator: isPlainString,
      $message: 'Nome deve conter apenas letras e números'
    }
  },
  phoneNumber: { required, isPhoneE164OrEmpty, minLength: minLength(13), maxLength: maxLength(15) },
};

// Vuelidate setup
const v$ = useVuelidate(rules, {
  channelName,
  phoneNumber,
});

const createChannel = async () => {
  await v$.value.$validate();
  
  if (v$.value.$invalid) {

    return;
  }

  const customAttributes = {};
  attributesWhatsapp.value.forEach(attribute => {
    customAttributes[attribute.attributeKey] = attribute.value;
    if (attribute.attributeKey === 'rejectCall' && attribute.value) {
      customAttributes['msgCall'] = attribute.msgCall || '';
    }
  });

  // ✅ Adicionar diretamente ao customAttributes
  customAttributes['chatwootConversationPending'] = false;




  try {
    const apiChannel = await store.dispatch('inboxes/createChannel', {
      name: channelName.value,
      channel: {
        type: 'api',
        phone_number: phoneNumber.value,
        provider: 'baileys',
        additional_attributes: customAttributes
      },
    });



    router.replace({
      name: 'settings_inbox_finish',
      params: { page: 'new', inbox_id: apiChannel.id },
    });
  } catch (error) {
    useAlert(error?.message || t('INBOX_MGMT.ADD.API_CHANNEL.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <!-- Container principal com largura limitada -->
  <div class="w-full max-w-4xl mx-auto">
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel()">
      <!-- Div única para os dois inputs -->
      <div class="flex-shrink-0 flex-grow-0 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label :class="{ error: v$.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
          <input
            v-model="channelName"
            type="text"
            class="w-full"
            :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
            @blur="v$.channelName.$touch"
          />
          <span v-if="v$.channelName.$error" class="message">
            Nome deve conter apenas letras e números (sem espaços ou acentos)
          </span>
        </label>

        <label :class="{ error: v$.phoneNumber.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
          <input
            v-model="phoneNumber"
            type="text"
            class="w-full"
            :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
            @blur="v$.phoneNumber.$touch"
          />
          <span v-if="v$.phoneNumber.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
          </span>
        </label>
      </div>

      <!-- Tabs usando shadcn/ui com CSS customizado -->
      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <Tabs v-model="activeTab" class="tabs-custom w-full">
          <TabsList class="tabs-list-custom">

            
            <TabsTrigger value="whatsapp" class="tabs-trigger-custom">
              Configurações do WhatsApp
            </TabsTrigger>
            
            <TabsTrigger value="cortexwoot" class="tabs-trigger-custom">
              Configurações do CortexWoot
            </TabsTrigger>


          </TabsList>

          
          <TabsContent value="whatsapp" class="tabs-content-custom">
            <div>
              <CustomAttributesEvolutionItem
                v-for="attribute in whatsappAttributes"
                :key="attribute.id"
                :attribute="attribute"
                @update-attribute="updateAttribute"
              />
            </div>
          </TabsContent>
          
          <TabsContent value="cortexwoot" class="tabs-content-custom">
            <div>
              <CustomAttributesEvolutionItem
                v-for="attribute in chatwootAttributes"
                :key="attribute.id"
                :attribute="attribute"
                @update-attribute="updateAttribute"
              />
            </div>
          </TabsContent>

        </Tabs>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>

<style scoped>
/* Tabs Container */
:deep(.tabs-list-custom) {
  @apply grid w-full grid-cols-2 bg-woot-25 dark:bg-slate-800;
  @apply border border-slate-200 dark:border-slate-700 rounded-lg p-1;
}

/* Tab Triggers */
:deep(.tabs-trigger-custom) {
  @apply flex items-center justify-center px-3 py-2 text-sm font-medium;
  @apply rounded-md transition-all duration-200;
  @apply text-slate-600 dark:text-slate-400;
  @apply hover:text-slate-900 dark:hover:text-slate-100;
  @apply hover:bg-slate-100 dark:hover:bg-slate-700;
}

:deep(.tabs-trigger-custom[data-state="active"]) {
  @apply bg-white dark:bg-slate-900 text-woot-600 dark:text-woot-400;
  @apply border border-slate-200 dark:border-slate-600;
  @apply shadow-sm;
}

/* Tab Content */
:deep(.tabs-content-custom) {
  @apply mt-4 p-6 bg-white dark:bg-slate-900;
  @apply border border-slate-200 dark:border-slate-700 rounded-lg;
  min-height: 360px;
}

/* Focus states */
:deep(.tabs-trigger-custom:focus-visible) {
  @apply outline-2 outline-offset-2 outline-woot-500;
}
</style>